/**
 * The end of the match, said properly.
 *
 * A match used to end with one line on the alert ticker, which scrolled away
 * with the ammunition warnings. Twenty minutes of play deserves more than that,
 * and more practically: a player who does not notice the match is over keeps
 * clicking at a simulation that has stopped listening.
 *
 * Presentation only. The outcome is decided in `sim/combat.ts` (R30) and this
 * reads the event.
 */

import type { Ctx, Subsystem } from '../core/registry.js'

export class Outcome implements Subsystem {
  static id = 'outcome'
  static deps = [] as const

  private readonly el: HTMLDivElement
  private readonly title: HTMLDivElement
  private readonly detail: HTMLDivElement

  /** Whose result this is. An observer is told who won rather than that they did. */
  localSlot = 0

  constructor(root: HTMLElement) {
    this.el = document.createElement('div')
    this.el.className = 'ba-outcome'
    this.title = document.createElement('div')
    this.title.className = 'ba-outcome-title'
    this.detail = document.createElement('div')
    this.detail.className = 'ba-outcome-detail'
    this.el.append(this.title, this.detail)
    root.append(this.el)
  }

  init(ctx: Ctx): void {
    ctx.on('match:over', (p) => {
      const q = p as { winner: number; reason: string }
      this.show(q.winner, q.reason)
    })
  }

  private show(winner: number, reason: string): void {
    if (reason !== 'win') {
      this.render('STALEMATE', 'Neither side could finish. The match ran out of time.')
      return
    }
    if (this.localSlot < 0) {
      this.render('MATCH OVER', `Player ${winner} wins.`)
      return
    }
    if (winner === this.localSlot) {
      this.render('VICTORY', 'Every enemy structure destroyed.')
    } else {
      this.render('DEFEATED', 'Your base has been destroyed.')
    }
  }

  private render(title: string, detail: string): void {
    this.title.textContent = title
    this.detail.textContent = detail
    this.el.classList.add('visible')
    this.el.classList.toggle('bad', title === 'DEFEATED')
  }

  dispose(): void {
    this.el.remove()
  }
}
